import { Object3D, Box3, Mesh, Sphere, Ray, Vector3 } from 'three';
import type {
  BufferGeometry,
  Color,
  DepthModes,
  Float32BufferAttribute,
  Group,
  Intersection,
  Matrix4,
  Raycaster,
} from 'three';

import { noop } from '@frontend/utils';

// eslint-disable-next-line no-restricted-imports
import type { ColorRGBArray, ColorRGBAArray } from '../../colors';
// eslint-disable-next-line no-restricted-imports
import type { Intersectable } from '../../renderer/renderer';
import { fillRepeatedPattern } from '../step/utils';
import type { STEPBody } from './STEPBody';
import { type FaceMetaData } from './STEPGeometry';
import type { Style } from './StepStyleAdapter';
import { StepStyleAdapter } from './StepStyleAdapter';
import type { ActiveMetaData } from './utils';
import { createActiveMetaData } from './utils';

interface HasComputeIntersections {
  _computeIntersections(
    raycaster: Raycaster,
    intersects: Intersection[],
    rayLocalSpace: Ray,
  ): void;
}

//PartialFace: StepFaceModel's face is broken up into chunks because of the max number of positions that can be in mesh
//* The geometry in StepFaceModel is actually in a packed set of BufferGeometry in the host. The vast majority of time, the
//  geometry for a single face is in a single BufferGeometry, but sometimes it staddles 2 or more BufferGeometry.
//  The StepFaceModel keeps track of the PartialFaces that are straddled across 1 or more BufferGeometry
class PartialFace extends Object3D {
  public parent: StepFaceModel;

  constructor(
    public geometry: BufferGeometry, //a proxy of a small range in one of the parent body's face buffers
    parent: StepFaceModel,
    public readonly bufNum: number,
  ) {
    super();
    this.parent = parent;

    //this.visible is deleted so we can use getter/setter (which is on prototype)
    //Note: There is a difference between this.visible and this.prototype.visible getter/setter
    //Similarly, do the same for this.matrixWorld
    const visible = this.parent.visible;
    /* eslint-disable @typescript-eslint/ban-ts-comment */
    //@ts-ignore
    delete this.visible; //delete this.visible property created by super() so getter/setter can be used
    this.parent.visible = visible;
    //@ts-ignore
    delete this.matrixWorld; //delete this.matrixWorld property created by super() so getter/setter can be used
    /* eslint-enable @typescript-eslint/ban-ts-comment */
  }

  public *positions() {
    const { start, count } = this.geometry.drawRange;
    const end = start + count * 3;
    const parentGeometry = this.parent.host.faceGeometries[this.bufNum];
    const parentPositions = (
      parentGeometry?.attributes?.position as Float32BufferAttribute | undefined
    )?.array as Float32Array | undefined;
    if (!parentPositions) {
      return;
    }
    for (let i = start; i < end; i++) {
      yield parentPositions[i];
    }
  }

  //Prefer not to use this.material. Use other getters/methods to access the material properties
  //@remarks: this.material is required by this.raycast()
  public get material() {
    return this.parent.host.faceMaterial;
  }

  /* eslint-disable @typescript-eslint/ban-ts-comment */

  //Note: this.matrixWorld is deleted after super() is called. It will be replaced by this getter/setter pair
  //matrixWorld is required for this.raycast() to work with bodies that are transformed into place
  //@ts-ignore
  public get matrixWorld(): Matrix4 {
    return this.parent.matrixWorld;
  }

  //Note: this.matrixWorld is deleted after super() is called. It will be replaced by this getter/setter pair
  //matrixWorld is required for this.raycast() to work with bodies that are transformed into place
  //@ts-ignore
  public set matrixWorld(v: Matrix4) {
    if (this.parent) {
      this.parent.matrixWorld = v;
    }
  }

  //Note: this.visible is deleted after super() is called. It will be replaced by this getter/setter pair
  //@ts-ignore
  public get visible(): boolean {
    return this.parent.visible;
  }

  //@ts-ignore
  public set visible(v: boolean) {
    if (this.parent) {
      this.parent.visible = v;
    }
  }
  /* eslint-enable @typescript-eslint/ban-ts-comment */

  /**
   * @remarks: this.raycast requires this.getVertexPosition() as of https://github.com/mrdoob/three.js/releases/tag/r148. Currently not defined in threejs types, but it is here https://threejs.org/docs/#api/en/objects/Mesh
   */
  public getVertexPosition(index: number, target: Vector3): Vector3 {
    return Mesh.prototype.getVertexPosition.call(this, index, target);
  }

  public _computeIntersections(
    raycaster: Raycaster,
    intersects: Intersection[],
    rayLocalSpace: Ray,
  ) {
    return (
      /*eslint-disable @typescript-eslint/no-explicit-any*/
      (
        Mesh.prototype as any as HasComputeIntersections
      )._computeIntersections.call(this, raycaster, intersects, rayLocalSpace)
      /*eslint-enable @typescript-eslint/no-explicit-any*/
    );
  }

  public raycast(raycaster: Raycaster, intersects: Intersection[]): void {
    //* raycast is delegated to Mesh's implementation
    //* Note: this.geometry is a Proxy to the parentGeometry with the boundBox, boundSphere and drawRange set based on this face's positions
    // in the parentGeometry. This way the mesh's raycast only looks at the positions that are in range for this face, and so that it considers
    // the appropriate boundBox and boundSphere for this face.
    Mesh.prototype.raycast.call(this, raycaster, intersects);
  }
}

const _sphere = new Sphere(); //temp sphere reused for intersection tests
const _ray = new Ray(); //temp ray reused for intersection tests
const _sphereHitAt = new Vector3(); //temp vector reused for intersection tests

/*
 * StepFaceModel provides some metadata about the face
 * @remarks StepFaceModel extends Object3D and overrides the raycast() method so it can be used to calculate this face's intersection by a Raycaster
 */
export class StepFaceModel extends Object3D implements Intersectable {
  /**
   * @remarks: boundBox is transformed in context of its body, whereas this.geometry.boundBox is NOT transformed!
   * this.boundBox is used by intersection-utils.ts to test if frustrum intersects this box
   */
  public readonly boundBox: Box3;
  public readonly boundSphere: Sphere;
  public readonly partialFaces: PartialFace[] = [];
  public readonly parent: Group; //The parent group for the body's faces this face belongs to
  public readonly activeMetaData: ActiveMetaData;
  public readonly metaData: Omit<
    FaceMetaData,
    'boundBox' | 'boundSphere' | 'bufferRanges'
  >;
  private _style?: Style;
  public canHaveAlpha = true;

  constructor(
    public readonly name: string,
    _metaData: FaceMetaData,
    public readonly host: STEPBody,
  ) {
    super();
    const { boundBox, boundSphere, bufferRanges, ...metaData } = _metaData;
    this.metaData = metaData; //boundBox and boundSphere removed from metaData
    this.activeMetaData = createActiveMetaData(metaData, host.transform);

    //cloned because this.boundBox will be transformed in context of its body
    //do not confuse this with this.geometry.boundBox which is not transformed
    this.boundBox = new Box3(
      new Vector3(...boundBox.min),
      new Vector3(...boundBox.max),
    );
    this.boundSphere = new Sphere(
      new Vector3(...boundSphere.center),
      boundSphere.radius,
    );

    this.parent = this.host.facesGroup;

    //this.visible is deleted so we can use getter/setter (which is on prototype)
    const visible = this.parent.visible;
    /* eslint-disable @typescript-eslint/ban-ts-comment */
    //@ts-ignore
    delete this.visible; //delete this.visible property created by super() so getter/setter can be used
    this.parent.visible = visible;
    //@ts-ignore
    delete this.matrixWorld; //delete this.visible property created by super() so getter/setter can be used
    /* eslint-enable @typescript-eslint/ban-ts-comment */

    //this.style is an adapter view into metaData
    this._style = new StepStyleAdapter(this, this.host);

    for (const {
      bufNum,
      bufStart,
      bufCount,
      boundBox,
      boundSphere,
    } of bufferRanges) {
      const drawRange: Readonly<BufferGeometry['drawRange']> = {
        start: bufStart / 3,
        count: bufCount / 3,
      };
      const _boundBox = new Box3(
        new Vector3(...boundBox.min),
        new Vector3(...boundBox.max),
      );
      const _boundSphere = new Sphere(
        new Vector3(...boundSphere.center),
        boundSphere.radius,
      );

      //Note:
      //* geometry is a proxy that is intended for use in PartialFace.prototype.raycast
      //* It is preferred to not use this.geometry elsewhere because care would be required to ensure the correct index in
      //  the parentGeometry is used. this.raycast is fine with this Proxy because it's implementation uses drawRange
      //  * As an example, caliper-utils.ts used to access `geometry.attributes.position` to construct a vector for the first position
      //    `this.firstPosition` is preferred because it takes care of using the correct index
      //  * Another example in caliper-utils.ts is getting the this.firstNormal
      //* geometry is a Proxy to the parentGeometry of the face and has the drawRange, boundBox and boundSphere specified/precomputed
      //  for this face.  And the index set to null.  The index is not necessary because the positions are always sequential for the drawRange.
      //  (By returning `null`, the indexing of the position attribute over the drawRange is a bit more efficient)
      //* A Proxy was chosen so the parentGeometry remains untouched. Currently threejs doesn't observe properties on BufferGeometry, and this.raycast() is synchronous
      //  so it is possible to temporarily swap out these values on the parentGeometry and restore them. But I want to avoid the possibility of WebGL seeing changes to the
      //  parentGeometry because it is in the scene.
      //* This proxied geometry is only used by raycast() to calculate the intersection of a ray with this StepFaceModel
      const geometry = new Proxy(this.host.faceGeometries[bufNum], {
        get(target, prop, receiver) {
          switch (prop) {
            case 'drawRange':
              return drawRange;
            case 'boundingBox':
              return _boundBox;
            case 'boundingSphere':
              return _boundSphere;
            case 'index':
              return null;
            case 'computeBoundingSphere':
            case 'computeBoundingBox':
            case 'computeTangents':
            case 'computeVertexNormals':
              return noop;
            default:
              return Reflect.get(target, prop, receiver);
          }
        },
      });
      this.partialFaces.push(new PartialFace(geometry, this, bufNum));
    }

    //Only faces that have their triangles entirely inside a single buffer can be transparent
    //* Some GPUs cannot render the transparent mesh when a face straddles more than one buffer.
    this.canHaveAlpha = bufferRanges.length <= 1;
  }

  public dispose() {
    if (!this._style) {
      return; //already disposed
    }
    delete this._style;
    this.partialFaces.length = 0;
  }

  public get style(): Style {
    if (!this._style) {
      throw new Error('This object was disposed');
    }
    return this._style;
  }

  public get rootBodyOrAssemblyInstanceId() {
    return this.host.body.rootBodyOrAssemblyInstanceId;
  }

  //Prefer not to use this.material. Use other getters/methods to access the material properties
  //@remarks: this.material is required by this.raycast()
  public get material() {
    return this.host.faceMaterial;
  }

  //@remarks: This is actually just the first geometry
  //It is defined to satisify `Intersectable` but not actually required because raycaster delegates to each `PartialFace`
  public get geometry() {
    return this.partialFaces[0].geometry as BufferGeometry; //actually just the first geometry
  }

  public get firstNormal(): Vector3 {
    const firstGeometry = this.partialFaces[0].geometry as BufferGeometry;
    return new Vector3().fromBufferAttribute(
      firstGeometry.attributes.normal as Float32BufferAttribute,
      firstGeometry.drawRange.start,
    );
  }

  public get firstPosition(): Vector3 {
    const firstGeometry = this.partialFaces[0].geometry;
    return new Vector3().fromBufferAttribute(
      firstGeometry.attributes.position as Float32BufferAttribute,
      firstGeometry.drawRange.start,
    );
  }

  //@remarks: this.index is used by getHighlightForHover
  public get index(): number {
    return this.metaData.index;
  }

  public get partUuid() {
    return this.host.partUuid;
  }

  public get parentName() {
    return this.host.name;
  }

  public get isMaterial(): boolean | undefined {
    return this.host.isMaterial;
  }

  public get transform() {
    return this.host.transform;
  }

  public get body() {
    return this.host.body;
  }

  /* eslint-disable @typescript-eslint/ban-ts-comment */

  //Note: this.matrixWorld is deleted after super() is called. It will be replaced by this getter/setter pair
  //matrixWorld is required for this.raycast() to work with bodies that are transformed into place
  //@ts-ignore
  public get matrixWorld(): Matrix4 {
    return this.parent.matrixWorld;
  }

  //Note: this.matrixWorld is deleted after super() is called. It will be replaced by this getter/setter pair
  //matrixWorld is required for this.raycast() to work with bodies that are transformed into place
  //@ts-ignore
  public set matrixWorld(v: Matrix4) {
    if (this.parent) {
      this.parent.matrixWorld = v;
    }
  }

  //Note: this.visible is deleted after super() is called. It will be replaced by this getter/setter pair
  //@ts-ignore
  public get visible(): boolean {
    //Face's visibility is controlled by the parent's visibility and the opacity
    //But when using the setter, we only modify the parent's visibility (which impacts ALL sibling faces)
    return this.parent.visible && this.opacity > 0;
  }

  //@ts-ignore
  public set visible(v: boolean) {
    //Currently a face's visibility is controlled by its parent facesGroup (all faces are visible or all hidden)
    //this.parent is initially undefined when super() is called
    if (this.parent) {
      this.parent.visible = v;
    }
  }
  /* eslint-enable @typescript-eslint/ban-ts-comment */

  public get defaultOpacity() {
    return this.host.opacity;
  }

  public get opacity(): number {
    const firstGeometry = this.partialFaces[0].geometry;
    const colors = (firstGeometry.attributes.color as Float32BufferAttribute)
      ?.array as Float32Array;
    //All triangles in a face will have the same opacity, so arbitrarily using the first
    return colors[firstGeometry.drawRange.start * 4 + 3];
  }

  public setOpacity(v: number, update = true) {
    if (this.canHaveAlpha || v === 1.0) {
      for (const partialFace of this.partialFaces) {
        const colors = (
          partialFace.geometry.attributes.color as Float32BufferAttribute
        )?.array as Float32Array;
        const start = partialFace.geometry.drawRange.start * 4;
        this.setColorAndOpacity(
          [colors[start], colors[start + 1], colors[start + 2], v],
          update,
        );
      }
    }
  }

  public get depthFunc(): DepthModes {
    //Currently a face's depthFunc is controlled by its parent faceMaterial (all faces share the same depthFunc color)
    return this.host.faceMaterial.depthFunc;
  }

  public set depthFunc(v: DepthModes) {
    //Currently a face's depthFunc is controlled by its parent faceMaterial (all faces share the same depthFunc color)
    this.host.faceMaterial.depthFunc = v;
  }

  public get emissive(): Color {
    //Currently a face's emissive color is controlled by its parent faceMaterial (all faces share the same emissive color)
    return this.host.faceMaterial.emissive;
  }

  public set emissive(v: Color) {
    //Currently a face's emissive color is controlled by its parent faceMaterial (all faces share the same emissive color)
    this.host.faceMaterial.emissive = v;
  }

  public get emissiveIntensity(): number {
    //Currently a face's emissiveIntensity is controlled by its parent faceMaterial (all faces share the same emissiveIntensity color)
    return this.host.faceMaterial.emissiveIntensity;
  }

  public set emissiveIntensity(v: number) {
    //Currently a face's emissiveIntensity is controlled by its parent faceMaterial (all faces share the same emissiveIntensity color)
    this.host.faceMaterial.emissiveIntensity = v;
  }

  public get defaultColor(): ColorRGBArray {
    return this.host.colorRGB;
  }

  //color rgb components in range of 0..1
  public get color(): ColorRGBArray {
    const firstGeometry = this.partialFaces[0].geometry;
    const colors = (firstGeometry.attributes.color as Float32BufferAttribute)
      ?.array as Float32Array;
    const start = firstGeometry.drawRange.start * 4;
    //Type is techincally Float32Array, but treating as tuple
    //return colors.slice(start, start + 3) as unknown as ColorRGBArray;
    return [colors[start], colors[start + 1], colors[start + 2]];
  }

  public get colorAndOpacity(): ColorRGBAArray {
    const firstGeometry = this.partialFaces[0].geometry;
    const colors = (firstGeometry.attributes.color as Float32BufferAttribute)
      ?.array as Float32Array;
    const start = firstGeometry.drawRange.start * 4;
    //Type is techincally Float32Array, but treating as tuple
    //return colors.slice(start, start + 4) as unknown as ColorRGBAArray;
    return [
      colors[start],
      colors[start + 1],
      colors[start + 2],
      colors[start + 3],
    ];
  }

  public setColorAndOpacity(
    faceColorAndOpacity: ColorRGBAArray,
    update = true,
  ) {
    for (const partialFace of this.partialFaces) {
      const colorAttribute = partialFace.geometry.attributes
        .color as Float32BufferAttribute;
      const colors = colorAttribute?.array as Float32Array;
      if (colors) {
        const { drawRange } = partialFace.geometry;
        const start = drawRange.start * 4; //drawRange.start is the face's start (not positions start)
        const count = drawRange.count;
        let needsUpdate = false;
        //Check red, green, blue and alpha values to see if an update is required
        const c: ColorRGBAArray = this.canHaveAlpha
          ? faceColorAndOpacity
          : [
              faceColorAndOpacity[0],
              faceColorAndOpacity[1],
              faceColorAndOpacity[2],
              1.0,
            ];
        if (
          c[0] !== colors[start] ||
          c[1] !== colors[start + 1] ||
          c[2] !== colors[start + 2] ||
          c[3] !== colors[start + 3]
        ) {
          fillRepeatedPattern(c, colors, start, count);
          needsUpdate = true;
        }
        if (update && needsUpdate && !colorAttribute.needsUpdate) {
          colorAttribute.needsUpdate = true;
          colorAttribute.version++;
        }
      }
    }
  }

  public *positions() {
    for (const partialFace of this.partialFaces) {
      yield* partialFace.positions();
    }
  }

  public translate(v: Vector3) {
    if (this.activeMetaData.center) {
      this.activeMetaData.center.add(v);
    }
    this.boundBox.translate(v);
  }

  public raycast(raycaster: Raycaster, intersects: Intersection[]): void {
    if (this.partialFaces.length > 1) {
      _sphere.copy(this.boundSphere);
      _sphere.applyMatrix4(this.matrixWorld);
      _ray.copy(raycaster.ray).recast(raycaster.near);
      //Always check the bounding sphere first for performance reasons. Most of the time there is no intersection
      if (
        !_sphere.containsPoint(_ray.origin) &&
        (_ray.intersectSphere(_sphere, _sphereHitAt) === null ||
          _ray.origin.distanceToSquared(_sphereHitAt) >
            (raycaster.far - raycaster.near) ** 2)
      ) {
        return;
      }
    }
    const partialIntersects: Intersection[] = [];
    for (const partialFace of this.partialFaces) {
      partialFace.raycast(raycaster, partialIntersects);
    }
    for (const partialIntersect of partialIntersects) {
      const { object, ...tail } = partialIntersect;
      intersects.push({ ...tail, object: this });
    }
  }
}
